import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from 'contexts/BookingContext';
import { useAuth } from 'contexts/AuthContext';
import { addCustomer, addReservations, addCompanion } from 'services/mewsService';
import { format } from 'date-fns';
import Toast from 'components/ux/toast/Toast';
import { COUNTRIES } from 'constants/countries';

function toMewsDateUtc(date) {
  // Dates originate from 'YYYY-MM-DD' inputs, which JS parses as UTC midnight.
  // UTC getters keep the calendar date stable regardless of the browser timezone
  // (local getters shift it back a day anywhere west of UTC).
  const d = new Date(date);
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}T00:00:00Z`;
}

const Booking = () => {
  const { state, dispatch } = useBooking();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const { checkIn, checkOut, config, roomLines, poReference, mainGuest, guests, notes } = state;

  if (!roomLines || roomLines.length === 0) {
    navigate('/availability');
    return null;
  }

  const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
  const totalRooms = roomLines.reduce((sum, l) => sum + l.quantity, 0);
  const totalPrice = roomLines.reduce((sum, l) => sum + l.totalPrice * l.quantity, 0);
  const currency = roomLines[0]?.currency || 'DKK';


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!poReference.trim()) {
      setError('Nordic PO reference is required.');
      return;
    }
    if (!mainGuest.firstName.trim() || !mainGuest.lastName.trim()) {
      setError('Main guest name is required.');
      return;
    }
    try {
      setSubmitting(true);

      const mainCustomerResult = await addCustomer({
        firstName: mainGuest.firstName,
        lastName: mainGuest.lastName,
        phone: mainGuest.phone || undefined,
        nationalityCode: mainGuest.nationalityCode || undefined,
      });

      const mainCustomerId = mainCustomerResult.Id;
      if (!mainCustomerId) {
        throw new Error('Failed to create main guest in Mews — no customer ID returned.');
      }

      // Exclude any guest that duplicates the main guest by name — the main
      // guest is on the reservation already; listing them again would double
      // the adult count and create a duplicate profile
      const mainName = `${mainGuest.firstName} ${mainGuest.lastName}`.trim().toLowerCase();
      const companionGuests = guests.filter(
        (g) => `${g.firstName || ''} ${g.lastName || ''}`.trim().toLowerCase() !== mainName
      );

      const guestCustomers = [];
      for (const guest of companionGuests) {
        if (guest.firstName.trim() && guest.lastName.trim()) {
          const guestResult = await addCustomer({
            firstName: guest.firstName,
            lastName: guest.lastName,
            birthDate: guest.birthDate || undefined,
            nationalityCode: guest.nationalityCode || undefined,
          });
          if (guestResult.Id) {
            guestCustomers.push({ id: guestResult.Id, name: `${guest.firstName} ${guest.lastName}` });
          }
        }
      }

      // Calculate adult/child counts from guests with actual names (matches customer creation filter)
      const validGuests = companionGuests.filter((g) => g.firstName.trim() && g.lastName.trim());
      const totalAdults = 1 + validGuests.filter((g) => !g.isChild).length; // +1 for main guest
      const totalChildren = validGuests.filter((g) => g.isChild).length;

      if (totalRooms === 0) {
        throw new Error('No rooms selected — cannot create reservation.');
      }

      // Breakfast is included automatically by the Nordic rate in Mews —
      // no product orders are sent (a product order would double the breakfast)
      const rooms = [];
      let adultsRemaining = totalAdults;
      let childrenRemaining = totalChildren;
      for (const line of roomLines) {
        for (let i = 0; i < line.quantity; i++) {
          const roomsLeft = totalRooms - rooms.length;
          if (roomsLeft <= 0) break;
          const adultsThisRoom = Math.max(1, Math.ceil(adultsRemaining / roomsLeft));
          const childrenThisRoom = Math.ceil(childrenRemaining / roomsLeft);
          adultsRemaining -= adultsThisRoom;
          childrenRemaining -= childrenThisRoom;

          rooms.push({
            categoryId: line.categoryId,
            rateId: line.rateId || config.nordicRate?.Id,
            startUtc: toMewsDateUtc(checkIn),
            endUtc: toMewsDateUtc(checkOut),
            customerId: mainCustomerId,
            adultCount: adultsThisRoom,
            childCount: childrenThisRoom,
            notes: line.flag === 'ALLOTMENT' ? 'ALLOTMENT' : undefined,
            availabilityBlockId: line.flag === 'ALLOTMENT' ? line.availabilityBlockId : undefined,
          });
        }
      }

      const result = await addReservations({
        groupName: `Nordic - ${poReference}`,
        poReference,
        rooms,
        notes: notes || undefined,
      });

      // Attach companion guests. Mews caps a room's companions at its person
      // count, so fill rooms in order (moving on when a room is full) and
      // surface any guest no room can take — never lose them silently
      const reservationIds = (result.Reservations || []).map((r) => r.Id).filter(Boolean);
      const unplacedGuests = [];
      let roomCursor = 0;
      for (const { id, name } of guestCustomers.filter((g) => g.id !== mainCustomerId)) {
        let placed = false;
        for (let i = 0; i < reservationIds.length && !placed; i++) {
          const index = (roomCursor + i) % reservationIds.length;
          try {
            await addCompanion(reservationIds[index], id);
            placed = true;
            roomCursor = index;
          } catch (compErr) {
            console.warn('Companion attach refused:', name, compErr.message);
          }
        }
        if (!placed) {
          unplacedGuests.push(name);
        }
      }

      dispatch({
        type: 'SET_CONFIRMATION',
        confirmation: {
          ...result,
          poReference,
          mainGuest,
          guests: companionGuests,
          companionWarnings: unplacedGuests,
          roomLines,
          checkIn,
          checkOut,
          totalPrice,
          currency,
        },
      });

      navigate('/confirmation');
    } catch (err) {
      if (err.status === 401) {
        logout();
        navigate('/login');
        return;
      }
      setError(`Booking failed: ${err.message}`);
      console.error('Booking error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const addGuestRow = () => {
    dispatch({
      type: 'ADD_GUEST',
      guest: { firstName: '', lastName: '', birthDate: '', nationalityCode: '', isChild: false },
    });
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate('/availability')}
        className="text-brand-gold hover:text-brand-gold-dark text-sm mb-4 inline-flex items-center gap-1 transition-colors"
      >
        <span>&larr;</span> Back to room selection
      </button>

      <h1 className="text-2xl font-medium text-brand-dark mb-1">Booking Details</h1>
      <p className="text-brand-muted text-sm mb-6 font-mono">
        {format(checkIn, 'dd MMM yyyy')} &rarr; {format(checkOut, 'dd MMM yyyy')} &middot; {nights}N &middot; {totalRooms}R
      </p>

      {error && (
        <div className="mb-4">
          <Toast message={error} type="error" dismissError={() => setError('')} />
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Room Summary */}
        <Section title="Room Summary">
          <div className="space-y-2">
            {roomLines.map((line, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span className="text-brand-dark">
                  {line.quantity}&times; {line.categoryName}
                  <span className="text-brand-muted ml-2 text-xs font-mono">({line.flag})</span>
                </span>
                <span className="font-medium font-mono">
                  {Math.round(line.totalPrice * line.quantity).toLocaleString('da-DK')} {line.currency}
                </span>
              </div>
            ))}
            <div className="border-t border-brand-border/50 pt-2 flex justify-between font-medium">
              <span>Total</span>
              <span className="font-mono">{Math.round(totalPrice).toLocaleString('da-DK')} {currency}</span>
            </div>
          </div>
        </Section>

        {/* Nordic Reference */}
        <Section title="Nordic Reference">
          <div>
            <label className="block text-sm font-medium text-brand-dark mb-2">
              PO Number <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              placeholder="PO#######"
              value={poReference}
              onChange={(e) => dispatch({ type: 'SET_PO_REFERENCE', poReference: e.target.value })}
              className="input-field font-mono"
              required
            />
            <p className="text-xs text-brand-muted mt-1.5">Nordic's purchase order number for this booking</p>
          </div>
        </Section>

        {/* Main Guest */}
        <Section title="Main Guest">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-brand-dark mb-2">
                First Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={mainGuest.firstName}
                onChange={(e) => dispatch({ type: 'SET_MAIN_GUEST', guest: { firstName: e.target.value } })}
                className="input-field"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-dark mb-2">
                Last Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={mainGuest.lastName}
                onChange={(e) => dispatch({ type: 'SET_MAIN_GUEST', guest: { lastName: e.target.value } })}
                className="input-field"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-dark mb-2">Telephone</label>
              <input
                type="tel"
                value={mainGuest.phone}
                onChange={(e) => dispatch({ type: 'SET_MAIN_GUEST', guest: { phone: e.target.value } })}
                placeholder="+45 12345678"
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-dark mb-2">Nationality</label>
              <select
                value={mainGuest.nationalityCode}
                onChange={(e) => dispatch({ type: 'SET_MAIN_GUEST', guest: { nationalityCode: e.target.value } })}
                className="input-field"
              >
                <option value="">Select...</option>
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>
        </Section>

        {/* Guest List */}
        <Section title="Guest List">
          <p className="text-sm text-brand-muted mb-4">
            Add each <strong>additional</strong> guest with full name and date of birth. Under 18 = child.
            The main guest above is included automatically &mdash; do not add them again here.
          </p>
          {guests.map((guest, index) => (
            <div key={index} className="grid grid-cols-12 gap-2 mb-3 items-end">
              <div className="col-span-3">
                {index === 0 && <label className="block text-xs font-medium text-brand-muted mb-1">First Name</label>}
                <input
                  type="text"
                  value={guest.firstName}
                  onChange={(e) =>
                    dispatch({ type: 'UPDATE_GUEST', index, updates: { firstName: e.target.value } })
                  }
                  placeholder="First"
                  className="input-field !py-2 text-sm"
                />
              </div>
              <div className="col-span-3">
                {index === 0 && <label className="block text-xs font-medium text-brand-muted mb-1">Last Name</label>}
                <input
                  type="text"
                  value={guest.lastName}
                  onChange={(e) =>
                    dispatch({ type: 'UPDATE_GUEST', index, updates: { lastName: e.target.value } })
                  }
                  placeholder="Last"
                  className="input-field !py-2 text-sm"
                />
              </div>
              <div className="col-span-3">
                {index === 0 && <label className="block text-xs font-medium text-brand-muted mb-1">Birth Date</label>}
                <input
                  type="date"
                  value={guest.birthDate}
                  onChange={(e) => {
                    const bd = e.target.value;
                    const age = bd ? Math.floor((Date.now() - new Date(bd)) / (365.25 * 24 * 60 * 60 * 1000)) : 0;
                    dispatch({
                      type: 'UPDATE_GUEST',
                      index,
                      updates: { birthDate: bd, isChild: age < 18 },
                    });
                  }}
                  className="input-field !py-2 text-sm"
                />
              </div>
              <div className="col-span-2">
                {index === 0 && <label className="block text-xs font-medium text-brand-muted mb-1">Nat.</label>}
                <select
                  value={guest.nationalityCode || ''}
                  onChange={(e) =>
                    dispatch({ type: 'UPDATE_GUEST', index, updates: { nationalityCode: e.target.value } })
                  }
                  className="input-field !py-2 text-sm"
                >
                  <option value="">--</option>
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.code}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-1 flex items-center gap-1">
                {guest.isChild && <span className="text-xs text-brand-gold">C</span>}
                <button
                  type="button"
                  onClick={() => dispatch({ type: 'REMOVE_GUEST', index })}
                  className="text-red-400 hover:text-red-600 text-lg transition-colors"
                  title="Remove guest"
                >
                  &times;
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={addGuestRow}
            className="text-sm text-brand-gold hover:text-brand-gold-dark transition-colors"
          >
            + Add guest
          </button>
        </Section>

        {/* Breakfast */}
        <Section title="Breakfast">
          <p className="text-sm text-brand-muted">
            Breakfast is included in the Nordic rate for all guests automatically.
          </p>
        </Section>

        {/* Notes */}
        <Section title="Extra Information">
          <textarea
            value={notes}
            onChange={(e) => dispatch({ type: 'SET_NOTES', notes: e.target.value })}
            placeholder="Allergies, lactose-free, special requests, etc."
            rows={3}
            className="input-field"
          />
        </Section>

        {/* Agency info */}
        <Section title="Travel Agency">
          <div className="bg-brand-teal/5 border border-brand-teal/20 rounded-lg p-4 flex items-center gap-4">
            <img src="/images/nordic-logo.webp" alt="Nordic" className="h-8 w-auto" />
            <div>
              <p className="text-sm text-brand-dark">
                <span className="font-medium">Nordic Info BV</span> &mdash; set automatically as travel agency
              </p>
              <p className="text-xs text-brand-muted mt-0.5">
                nordic.be &middot; Steenhuffeldorp 14, Londerzeel, Belgium
              </p>
            </div>
          </div>
        </Section>

        {/* Warning: more rooms than adults — each room is registered with min 1 adult in Mews */}
        {totalRooms > 1 + guests.filter((g) => g.firstName.trim() && g.lastName.trim() && !g.isChild).length && (
          <div className="mt-6 p-4 bg-amber-50 border border-amber-300 rounded-lg text-sm">
            <p className="font-medium text-amber-800">More rooms than adults</p>
            <p className="text-amber-700 mt-1">
              You have selected {totalRooms} rooms but listed fewer adults.
              Each room will be registered with at least 1 adult in Mews.
              Please verify the room count and guest list are correct.
            </p>
          </div>
        )}

        {/* Submit */}
        <div className="mt-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pt-6 border-t border-brand-border/50">
          <div>
            <p className="text-sm text-brand-muted font-mono">
              {totalRooms}R &middot; {nights}N &middot; PO: {poReference || '—'}
            </p>
            <p className="text-2xl font-medium text-brand-dark font-mono">
              {Math.round(totalPrice).toLocaleString('da-DK')} {currency}
            </p>
          </div>
          <button
            type="submit"
            disabled={submitting}
            className={`btn-gold ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <span className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                Creating reservation...
              </span>
            ) : (
              'Confirm Booking'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="mb-7">
    <h2 className="text-base font-medium text-brand-dark mb-3 pb-2 border-b border-brand-border/50">{title}</h2>
    {children}
  </div>
);

export default Booking;
